import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, Play, Pause } from "lucide-react";
import Logo from "@/components/Logo";
import heroBackground from "@/assets/hero-background.jpg";
import { useHeroVideo } from "@/hooks/useHeroVideo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BookingModal from "@/components/BookingModal";
import { useTranslation } from "react-i18next";
import { triggerHapticFeedback } from "@/lib/haptics";

const Hero = () => {
  const { videoUrl } = useHeroVideo();
  const { t } = useTranslation();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleBookingClick = () => {
    triggerHapticFeedback('medium');
    setBookingModalOpen(true);
  };

  const toggleVideoPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (video.paused) {
      video.play();
      setIsVideoPlaying(true);
    } else {
      video.pause();
      setIsVideoPlaying(false);
    }
    triggerHapticFeedback('light');
  };

  return (
    <header className="relative w-full min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Full-width video background */}
      <div 
        className="absolute inset-0 z-0 w-full h-full bg-cover bg-center"
        style={{ backgroundImage: `url(${heroBackground})` }}
      >
        <video 
          ref={videoRef}
          id="hero-video"
          autoPlay 
          muted 
          playsInline 
          loop
          className="w-full h-full object-cover"
          poster={heroBackground}
          {...({ fetchpriority: "high" } as any)}
          aria-label="Guestly Homes corporate housing video - modern furnished apartments for business travelers and extended stays"
          onError={(e) => {
            // Silently handle video loading errors and fall back to poster image
            const video = e.currentTarget;
            video.style.display = 'none';
          }}
        >
          {videoUrl && <source src={videoUrl} type="video/mp4" />}
        </video>
        {/* Dark overlay for text readability (35-45% opacity) */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0 bg-black/40" 
        />
      </div>

      {/* Logo */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-8 z-20">
        <Logo width={48} height={18} />
      </div>

      {/* Language Switcher */}
      <div className="absolute top-4 right-4 sm:top-8 sm:right-8 z-20">
        <LanguageSwitcher />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 text-center sm:text-right max-w-4xl">
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl mb-4 sm:mb-6 text-white leading-[1.1] font-bold"
        >
          {t('hero.headline')}
        </motion.h1>
        <motion.p 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0, 0.2, 1] }}
          className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-8 sm:mb-12 text-white font-normal"
        >
          {t('hero.subtitle')} <br />
          {t('hero.subtitleLine2')}
        </motion.p>
        
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.2, 1] }}
          className="flex flex-col sm:flex-row gap-4 justify-center sm:justify-end items-stretch sm:items-end max-w-md sm:max-w-none mx-auto"
        >
          <Button 
            size="lg" 
            className="text-base sm:text-lg px-8 py-6 sm:py-7 font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 w-full sm:w-auto rounded-full min-h-[56px] flex items-center justify-center gap-2"
            onClick={handleBookingClick}
          >
            <Phone className="h-5 w-5" />
            {t('hero.bookCall')}
          </Button>
          <a href="https://guestlyhomes.com/search-result/" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button 
              variant="outline" 
              size="lg" 
              className="text-base sm:text-lg px-8 py-6 sm:py-7 font-semibold border-2 transition-all duration-300 hover:scale-105 w-full rounded-full min-h-[56px] bg-background/10 backdrop-blur-sm hover:bg-background/20"
            >
              {t('hero.seeHomes')}
            </Button>
          </a>
        </motion.div>
      </div>

      {/* Video Play/Pause Control */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.8 }}
        onClick={toggleVideoPlayback}
        className="absolute bottom-6 right-6 z-20 bg-background/20 backdrop-blur-md hover:bg-background/30 text-foreground p-4 rounded-full transition-all duration-300 hover:scale-110 shadow-lg"
        aria-label={isVideoPlaying ? "Pause video" : "Play video"}
      >
        {isVideoPlaying ? (
          <Pause className="h-6 w-6" />
        ) : (
          <Play className="h-6 w-6" fill="currentColor" />
        )}
      </motion.button>

      <BookingModal open={bookingModalOpen} onOpenChange={setBookingModalOpen} />
    </header>
  );
};

export default Hero;
